import React, { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import PublicNav from "./PublicNav";

/* ─────────────────────────────────────────────────────────────
   MiniNavbar - Floating navbar for auth pages
───────────────────────────────────────────────────────────── */
export function MiniNavbar() {
  return <PublicNav />;
}

/* ─────────────────────────────────────────────────────────────
   WebGL Components
───────────────────────────────────────────────────────────── */

const CanvasRevealEffect = ({
  animationSpeed = 10,
  opacities = [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1],
  colors = [[0, 255, 255]],
  dotSize,
  showGradient = true,
  reverse = false,
}) => {
  return (
    <div style={{ height: '100%', position: 'relative', width: '100%' }}>
      <div style={{ height: '100%', width: '100%' }}>
        <DotMatrix
          colors={colors ?? [[0, 255, 255]]}
          dotSize={dotSize ?? 3}
          opacities={opacities ?? [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1]}
          shader={`${reverse ? 'u_reverse_active' : 'false'}_;animation_speed_factor_${animationSpeed.toFixed(1)}_;`}
          center={["x", "y"]}
        />
      </div>
      {showGradient && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #000, transparent)' }} />
      )}
    </div>
  );
};

const DotMatrix = ({
  colors = [[0, 0, 0]],
  opacities = [0.04, 0.04, 0.04, 0.04, 0.04, 0.08, 0.08, 0.08, 0.08, 0.14],
  totalSize = 20,
  dotSize = 2,
  shader = "",
  center = ["x", "y"],
}) => {
  const uniforms = useMemo(() => {
    let colorsArray = [colors[0], colors[0], colors[0], colors[0], colors[0], colors[0]];
    if (colors.length === 2) {
      colorsArray = [colors[0], colors[0], colors[0], colors[1], colors[1], colors[1]];
    } else if (colors.length === 3) {
      colorsArray = [colors[0], colors[0], colors[1], colors[1], colors[2], colors[2]];
    }
    return {
      u_colors: { value: colorsArray.map((c) => [c[0] / 255, c[1] / 255, c[2] / 255]), type: "uniform3fv" },
      u_opacities: { value: opacities, type: "uniform1fv" },
      u_total_size: { value: totalSize, type: "uniform1f" },
      u_dot_size: { value: dotSize, type: "uniform1f" },
      u_reverse: { value: shader.includes("u_reverse_active") ? 1 : 0, type: "uniform1i" },
    };
  }, [colors, opacities, totalSize, dotSize, shader]);

  return (
    <Shader
      source={`
        precision mediump float;
        in vec2 fragCoord;
        uniform float u_time;
        uniform float u_opacities[10];
        uniform vec3 u_colors[6];
        uniform float u_total_size;
        uniform float u_dot_size;
        uniform vec2 u_resolution;
        uniform int u_reverse;
        out vec4 fragColor;
        float PHI = 1.61803398874989484820459;
        float random(vec2 xy) { return fract(tan(distance(xy * PHI, xy) * 0.5) * xy.x); }
        void main() {
          vec2 st = fragCoord.xy;
          ${center.includes("x") ? "st.x -= abs(floor((mod(u_resolution.x, u_total_size) - u_dot_size) * 0.5));" : ""}
          ${center.includes("y") ? "st.y -= abs(floor((mod(u_resolution.y, u_total_size) - u_dot_size) * 0.5));" : ""}
          float opacity = step(0.0, st.x) * step(0.0, st.y);
          vec2 st2 = vec2(int(st.x / u_total_size), int(st.y / u_total_size));
          float frequency = 5.0;
          float show_offset = random(st2);
          float rand = random(st2 * floor((u_time / frequency) + show_offset + frequency));
          opacity *= u_opacities[int(rand * 10.0)];
          opacity *= 1.0 - step(u_dot_size / u_total_size, fract(st.x / u_total_size));
          opacity *= 1.0 - step(u_dot_size / u_total_size, fract(st.y / u_total_size));
          vec3 color = u_colors[int(show_offset * 6.0)];
          float animation_speed_factor = 0.5;
          vec2 center_grid = u_resolution / 2.0 / u_total_size;
          float dist_from_center = distance(center_grid, st2);
          float timing_offset_intro = dist_from_center * 0.01 + (random(st2) * 0.15);
          float max_grid_dist = distance(center_grid, vec2(0.0, 0.0));
          float timing_offset_outro = (max_grid_dist - dist_from_center) * 0.02 + (random(st2 + 42.0) * 0.2);
          float current_timing_offset;
          if (u_reverse == 1) {
            current_timing_offset = timing_offset_outro;
            opacity *= 1.0 - step(current_timing_offset, u_time * animation_speed_factor);
            opacity *= clamp((step(current_timing_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);
          } else {
            current_timing_offset = timing_offset_intro;
            opacity *= step(current_timing_offset, u_time * animation_speed_factor);
            opacity *= clamp((1.0 - step(current_timing_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);
          }
          fragColor = vec4(color, opacity);
          fragColor.rgb *= fragColor.a;
        }`}
      uniforms={uniforms}
      maxFps={60}
    />
  );
};

const ShaderMaterial = ({ source, uniforms }) => {
  const { size } = useThree();
  const ref = useRef(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.material.uniforms.u_time.value = clock.getElapsedTime();
  });

  const material = useMemo(() => {
    const preparedUniforms = {};
    for (const name in uniforms) {
      const u = uniforms[name];
      if (u.type === "uniform1f") preparedUniforms[name] = { value: u.value };
      else if (u.type === "uniform1i") preparedUniforms[name] = { value: u.value };
      else if (u.type === "uniform1fv") preparedUniforms[name] = { value: u.value };
      else if (u.type === "uniform3fv") preparedUniforms[name] = { value: u.value.map((v) => new THREE.Vector3().fromArray(v)) };
    }
    preparedUniforms["u_time"] = { value: 0 };
    preparedUniforms["u_resolution"] = { value: new THREE.Vector2(size.width * 2, size.height * 2) };

    return new THREE.ShaderMaterial({
      vertexShader: `precision mediump float; uniform vec2 u_resolution; out vec2 fragCoord; void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); fragCoord = (position.xy + vec2(1.0)) * 0.5 * u_resolution; fragCoord.y = u_resolution.y - fragCoord.y; }`,
      fragmentShader: source,
      uniforms: preparedUniforms,
      glslVersion: THREE.GLSL3,
      blending: THREE.CustomBlending,
      blendSrc: THREE.SrcAlphaFactor,
      blendDst: THREE.OneFactor,
    });
  }, [size.width, size.height, source, uniforms]);

  return (
    <mesh ref={ref}>
      <planeGeometry args={[2, 2]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
};

const Shader = ({ source, uniforms }) => (
  <Canvas style={{ position: 'absolute', inset: 0, height: '100%', width: '100%' }}>
    <ShaderMaterial source={source} uniforms={uniforms} />
  </Canvas>
);

/* ─────────────────────────────────────────────────────────────
   Main SignInFlow Component
───────────────────────────────────────────────────────────── */
export function SignInFlow({ onSuccess, onClose }) {
  const [step, setStep] = useState("email"); // email, loading, success, error
  const [initialCanvasVisible, setInitialCanvasVisible] = useState(true);
  const [reverseCanvasVisible, setReverseCanvasVisible] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleMicrosoftSignIn = async () => {
    // Immediately show loading state
    setStep("loading");

    try {
      // Actually call the OAuth - onSuccess returns when complete
      if (onSuccess) {
        await onSuccess();
      }
      // OAuth succeeded - now show success animation
      setReverseCanvasVisible(true);
      setTimeout(() => setInitialCanvasVisible(false), 50);
      setStep("success");
    } catch (err) {
      // OAuth failed or was cancelled
      console.error("Sign-in error:", err);
      setErrorMessage(err.message || "Sign-in failed. Please try again.");
      setStep("error");
    }
  };

  const handleRetry = () => {
    setStep("email");
    setErrorMessage("");
  };

  // Container - full screen, black background
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: '100vh',
    height: '100vh',
    backgroundColor: '#000',
    position: 'relative',
    overflow: 'hidden',
  };

  // Canvas layer - behind everything
  const canvasLayerStyle = {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  };

  // Gradient overlays
  const radialOverlayStyle = {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at center, rgba(0,0,0,0.8) 0%, transparent 100%)',
    pointerEvents: 'none',
  };

  const topGradientStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '33%',
    background: 'linear-gradient(to bottom, #000, transparent)',
    pointerEvents: 'none',
  };

  // Content layer - above canvas
  const contentLayerStyle = {
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  };

  // Main content area - centered
  const mainContentStyle = {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1.5rem',
    paddingTop: '6rem', // Account for navbar
  };

  // Form container
  const formContainerStyle = {
    width: '100%',
    maxWidth: '400px',
  };

  // Form content wrapper
  const formContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    textAlign: 'center',
  };

  // Heading styles
  const headingStyle = {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: '#fff',
    margin: 0,
  };

  // Gradient text for emphasis
  const gradientTextStyle = {
    background: 'linear-gradient(135deg, #3b82f6, #22d3ee)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 300,
    margin: '0.5rem 0 0 0',
  };

  // Microsoft button
  const microsoftButtonStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    backgroundColor: buttonHovered ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
    color: '#fff',
    border: `1px solid ${buttonHovered ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'}`,
    borderRadius: '9999px',
    padding: '1rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    fontFamily: 'inherit',
  };

  // Terms text
  const termsStyle = {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.4)',
    lineHeight: 1.6,
    marginTop: '2rem',
  };

  const termsLinkStyle = {
    color: 'rgba(255, 255, 255, 0.4)',
    textDecoration: 'underline',
  };

  // Success icon
  const successIconStyle = {
    margin: '0 auto',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #fff, rgba(255, 255, 255, 0.7))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 60px rgba(255, 255, 255, 0.3)',
  };

  return (
    <div style={containerStyle}>
      {/* Floating Navbar */}
      <MiniNavbar />

      {/* Canvas Background Layer */}
      <div style={canvasLayerStyle}>
        {initialCanvasVisible && (
          <div style={{ position: 'absolute', inset: 0 }}>
            <CanvasRevealEffect
              animationSpeed={3}
              colors={[[255, 255, 255], [255, 255, 255]]}
              dotSize={6}
              reverse={false}
            />
          </div>
        )}
        {reverseCanvasVisible && (
          <div style={{ position: 'absolute', inset: 0 }}>
            <CanvasRevealEffect
              animationSpeed={4}
              colors={[[255, 255, 255], [255, 255, 255]]}
              dotSize={6}
              reverse={true}
            />
          </div>
        )}
        <div style={radialOverlayStyle} />
        <div style={topGradientStyle} />
      </div>

      {/* Content Layer */}
      <div style={contentLayerStyle}>
        {/* Main Content */}
        <div style={mainContentStyle}>
          <div style={formContainerStyle}>
            <AnimatePresence mode="wait">
              {step === "email" && (
                <motion.div
                  key="email-step"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  style={formContentStyle}
                >
                  <div>
                    <h1 style={headingStyle}><span style={gradientTextStyle}>Automate</span> Your Outreach</h1>
                    <p style={subtitleStyle}>Sign in to get started</p>
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <button
                      onClick={handleMicrosoftSignIn}
                      style={microsoftButtonStyle}
                      onMouseEnter={() => setButtonHovered(true)}
                      onMouseLeave={() => setButtonHovered(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" fill="none">
                        <rect x="17" y="17" width="10" height="10" fill="#FEBA08"/>
                        <rect x="5" y="17" width="10" height="10" fill="#05A6F0"/>
                        <rect x="17" y="5" width="10" height="10" fill="#80BC06"/>
                        <rect x="5" y="5" width="10" height="10" fill="#F25325"/>
                      </svg>
                      <span>Continue with Microsoft</span>
                    </button>
                  </div>

                  <p style={termsStyle}>
                    By signing in, you agree to our{" "}
                    <Link to="#terms" style={termsLinkStyle}>Terms of Service</Link>
                    {" "}and{" "}
                    <Link to="#privacy" style={termsLinkStyle}>Privacy Policy</Link>
                  </p>
                </motion.div>
              )}

              {step === "loading" && (
                <motion.div
                  key="loading-step"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  style={formContentStyle}
                >
                  <div>
                    <h1 style={headingStyle}>Signing in...</h1>
                    <p style={subtitleStyle}>Complete the sign-in in the popup</p>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
                    <div style={{
                      width: 40, height: 40,
                      border: '3px solid rgba(255,255,255,0.2)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                  </div>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <button
                    onClick={handleRetry}
                    style={{ ...microsoftButtonStyle, backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    ← Cancel
                  </button>
                </motion.div>
              )}

              {step === "error" && (
                <motion.div
                  key="error-step"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={formContentStyle}
                >
                  <div>
                    <h1 style={headingStyle}>Sign-in Failed</h1>
                    <p style={{ ...subtitleStyle, color: '#fca5a5' }}>{errorMessage}</p>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      onClick={handleRetry}
                      style={microsoftButtonStyle}
                    >
                      Try Again
                    </button>
                  </div>
                </motion.div>
              )}

              {step === "success" && (
                <motion.div
                  key="success-step"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: 0.3 }}
                  style={formContentStyle}
                >
                  <div>
                    <h1 style={headingStyle}>You're in!</h1>
                    <p style={subtitleStyle}>Redirecting...</p>
                  </div>

                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    style={{ padding: '2rem 0' }}
                  >
                    <div style={successIconStyle}>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 40, height: 40, color: '#000' }} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CanvasRevealEffect };
